import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FaChartBar, FaCog, FaSave, FaCreditCard, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import Header from '../Header';
import api from '../services/apiService';
import AcademyPersonSearch from './AcademyPersonSearch';
import AcademyPlayers from './AcademyPlayers';
import AcademyUploads from './AcademyUploads';
import AcademyTeamPicker from './AcademyTeamPicker';
import MembershipFeeModal from '../components/MembershipFeeModal';
import { ACADEMY_ADMIN_API, academyTreasurerPathFor, getCachedAcademies, personHasKnownRole, personRoles } from '../utils/academyAccess';
import { authService } from '../services/authService';
import { compareAcademyTeams, loadAcademyDirectory } from '../utils/academyTeamSort';
import '../scss/admin/AcademySubscriptions.scss';
import '../scss/membershipParent.scss';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const poundsFromPence = (pence) => (Number(pence || 0) / 100).toFixed(2);

const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

const academyProductLabel = (name) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) {
    return 'Monthly Subscription';
  }
  if (/monthly subscription$/i.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed} Monthly Subscription`;
};

const STRIPE_CARD_PERCENT = 1.5;
const STRIPE_CARD_FIXED_PENCE = 20;

const formatGbpFromPence = (pence) => {
  const value = Number(pence) || 0;
  const formatted = `£${(Math.abs(value) / 100).toFixed(2)}`;
  return value < 0 ? `−${formatted}` : formatted;
};

const penceFromPounds = (pounds) => {
  const parsed = Math.round(Number(pounds) * 100);
  return parsed > 0 ? parsed : 0;
};

const stripeCardFeePence = (chargePence) => {
  if (chargePence < 1) {
    return 0;
  }
  return Math.round(chargePence * STRIPE_CARD_PERCENT / 100) + STRIPE_CARD_FIXED_PENCE;
};

const defaultFeeBreakdown = (amountPounds, percent = 5, feeIncluded = false) => {
  const enteredPence = penceFromPounds(amountPounds);
  const rate = Number(percent) > 0 ? Number(percent) : 5;
  const feePence = enteredPence > 0 ? Math.round(enteredPence * rate / 100) : 0;
  const clubPence = feeIncluded ? Math.max(0, enteredPence - feePence) : enteredPence;
  const parentPence = feeIncluded ? enteredPence : enteredPence + feePence;
  const stripePence = stripeCardFeePence(parentPence);
  return {
    enteredPence,
    clubPence,
    fwPence: feePence,
    stripePence,
    fwKeepsPence: feePence - stripePence,
    parentPence,
  };
};

const seasonInclusiveRange = (startMonth, endMonth, now = new Date()) => {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const wraps = startMonth > endMonth;
  let startYear;
  let endYear;
  if (wraps) {
    startYear = month >= startMonth ? year : year - 1;
    endYear = startYear + 1;
  } else {
    startYear = year;
    endYear = year;
    if (month < startMonth) {
      startYear -= 1;
      endYear -= 1;
    }
  }
  const startName = MONTHS.find((row) => row.value === startMonth)?.label || '';
  const endName = MONTHS.find((row) => row.value === endMonth)?.label || '';
  const lastDay = lastDayOfMonth(endYear, endMonth);
  return `1 ${startName} ${startYear} to ${lastDay} ${endName} ${endYear}`;
};

const AcademySubscriptionEdit = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingTeams, setSavingTeams] = useState(false);
  const [savingPerson, setSavingPerson] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [teamAmounts, setTeamAmounts] = useState({});
  const [people, setPeople] = useState([]);
  const [activeTab, setActiveTab] = useState(
    searchParams.get('stripe') ? 'stripe' : 'club'
  );
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [treasurerPath, setTreasurerPath] = useState(() => (
    academyTreasurerPathFor(getCachedAcademies(), id, authService.isSuperAdmin())
  ));
  const [loadedId, setLoadedId] = useState('');
  const loadRequest = useRef(0);
  const [form, setForm] = useState({
    name: '',
    amount_pounds: '0.00',
    platform_fee_percent: 5,
    fee_included: false,
    season_start_month: 8,
    season_end_month: 7,
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    stripe_connect_account: '',
    stripe_connected: false,
    season_label: '',
    sibling_discount_second_percent: '0',
    sibling_discount_third_percent: '0',
  });

  const load = async (academyId = id) => {
    const requestId = ++loadRequest.current;
    setLoading(true);
    setError('');
    try {
      const academyRes = await api.get(`${ACADEMY_ADMIN_API}/get_academy_subscription.php?id=${academyId}`);
      if (requestId !== loadRequest.current) {
        return;
      }
      if (!academyRes.data.success) {
        throw new Error(academyRes.data.message || 'Could not load academy');
      }
      const academy = academyRes.data.academy;
      if (String(academy.id) !== String(academyId)) {
        throw new Error('Loaded the wrong academy');
      }
      setLoadedId(String(academy.id));
      setForm({
        name: academy.name || '',
        amount_pounds: poundsFromPence(academy.amount_pence),
        platform_fee_percent: Number(academy.platform_fee_percent ?? 5),
        fee_included: !!academy.fee_included,
        season_start_month: academy.season_start_month || 8,
        season_end_month: academy.season_end_month || 7,
        contact_name: academy.contact_name || '',
        contact_email: academy.contact_email || '',
        contact_phone: academy.contact_phone || '',
        stripe_connect_account: academy.stripe_connect_account || '',
        stripe_connected: !!academy.stripe_connected,
        season_label: academy.season_label || '',
        sibling_discount_second_percent: String(academy.sibling_discount_second_percent ?? 0),
        sibling_discount_third_percent: String(academy.sibling_discount_third_percent ?? 0),
      });
      setSelectedTeamIds((academy.teams || []).map((team) => Number(team.team_id)));
      const amounts = {};
      (academy.teams || []).forEach((team) => {
        const teamId = Number(team.team_id);
        if (!teamId) {
          return;
        }
        amounts[teamId] = team.amount_pence > 0 ? poundsFromPence(team.amount_pence) : '';
      });
      setTeamAmounts(amounts);
      setPeople(academy.admins || []);
      const directory = await loadAcademyDirectory(api);
      const byId = new Map();
      directory.forEach((team) => byId.set(Number(team.team_id), team));
      (academy.teams || []).forEach((team) => {
        const teamId = Number(team.team_id);
        if (teamId && !byId.has(teamId)) {
          byId.set(teamId, team);
        }
      });
      setAllTeams(Array.from(byId.values()));
    } catch (err) {
      if (requestId !== loadRequest.current) {
        return;
      }
      setError(err.response?.data?.message || err.message || 'Could not load academy');
    } finally {
      if (requestId === loadRequest.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load(id);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setTreasurerPath(academyTreasurerPathFor(getCachedAcademies(), id, authService.isSuperAdmin()));
    const loadAccess = async () => {
      try {
        const response = await api.get(`${ACADEMY_ADMIN_API}/get_my_academy_subscriptions.php`);
        const list = response.data?.success ? (response.data.academies || []) : [];
        if (!cancelled) {
          setTreasurerPath(academyTreasurerPathFor(list, id, authService.isSuperAdmin()));
        }
      } catch (err) {
        if (!cancelled && !authService.isSuperAdmin()) {
          setTreasurerPath(null);
        }
      }
    };
    loadAccess();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const stripe = searchParams.get('stripe');
    const message = searchParams.get('message');
    if (stripe === 'connected') {
      setSuccess('Stripe account connected.');
      setActiveTab('stripe');
      load();
    } else if (stripe === 'error') {
      setActiveTab('stripe');
      setError(message || 'Could not connect Stripe');
    }
  }, [searchParams]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async (e, { includeContact = true } = {}) => {
    e.preventDefault();
    if (String(loadedId) !== String(id)) {
      setError('This page is still showing another academy. Wait for it to finish loading, then save again.');
      return;
    }
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        id: Number(id),
        name: form.name,
        amount_pounds: form.amount_pounds,
        fee_included: form.fee_included ? 1 : 0,
        season_start_month: Number(form.season_start_month),
        season_end_month: Number(form.season_end_month),
        enabled: true,
        sibling_discount_second_percent: Number(form.sibling_discount_second_percent || 0),
        sibling_discount_third_percent: Number(form.sibling_discount_third_percent || 0),
        team_amounts: selectedTeamIds.map((teamId) => ({
          team_id: teamId,
          amount_pounds: teamAmounts[teamId] ?? '',
        })),
      };
      if (includeContact) {
        payload.contact_name = form.contact_name;
        payload.contact_email = form.contact_email;
        payload.contact_phone = form.contact_phone;
      }
      const response = await api.post(`${ACADEMY_ADMIN_API}/save_academy_subscription.php`, payload);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Save failed');
      }
      setSuccess('Settings saved');
      if (response.data.academy?.season_label) {
        updateField('season_label', response.data.academy.season_label);
      }
      if (Array.isArray(response.data.academy?.teams)) {
        const amounts = {};
        response.data.academy.teams.forEach((team) => {
          const teamId = Number(team.team_id);
          if (!teamId) {
            return;
          }
          amounts[teamId] = team.amount_pence > 0 ? poundsFromPence(team.amount_pence) : '';
        });
        setTeamAmounts(amounts);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTeams = async () => {
    setError('');
    setSuccess('');
    setSavingTeams(true);
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/update_academy_subscription_teams.php`, {
        id: Number(id),
        team_ids: selectedTeamIds,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not save teams');
      }
      setSuccess('Teams updated');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save teams');
    } finally {
      setSavingTeams(false);
    }
  };

  const handleSavePeople = async () => {
    if (!people.length) {
      setError('Assign at least one person');
      return;
    }
    if (people.some((row) => !personHasKnownRole(row))) {
      setError('Choose at least one role for each person');
      return;
    }
    setError('');
    setSuccess('');
    setSavingPerson(true);
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/update_academy_subscription_admins.php`, {
        id: Number(id),
        people: people.map((row) => ({
          person_type: row.person_type,
          person_id: row.person_id,
          roles: personRoles(row),
          role: personRoles(row)[0] || '',
        })),
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not save assigned people');
      }
      setPeople(response.data.admins || people);
      setSuccess('Assigned people updated');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save assigned people');
    } finally {
      setSavingPerson(false);
    }
  };

  const handleConnect = async () => {
    setError('');
    setConnecting(true);
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/stripe_connect_oauth_start.php`, {
        id: Number(id),
        app_origin: window.location.origin,
      });
      if (!response.data.success || !response.data.authorize_url) {
        throw new Error(response.data.message || 'Could not start Stripe Connect');
      }
      window.location.href = response.data.authorize_url;
    } catch (err) {
      setConnecting(false);
      setError(err.response?.data?.message || err.message || 'Could not start Stripe Connect');
    }
  };

  const toggleTeam = (teamId) => {
    setSelectedTeamIds((current) => (
      current.includes(teamId)
        ? current.filter((value) => value !== teamId)
        : [...current, teamId]
    ));
  };

  const feeBreakdown = defaultFeeBreakdown(
    form.amount_pounds,
    form.platform_fee_percent,
    form.fee_included
  );

  const feeTeams = useMemo(() => selectedTeamIds
    .map((teamId) => {
      const team = allTeams.find((row) => Number(row.team_id) === Number(teamId));
      return team || { team_id: teamId, team_name: `Team ${teamId}` };
    })
    .sort(compareAcademyTeams), [selectedTeamIds, allTeams]);

  const subscriptionsReady = !!form.stripe_connected;

  return (
    <div className="academy-subscriptions container">
      <Header />
      <div className="page-header">
        <h1>
          <FaCog /> Academy settings
        </h1>
        {treasurerPath ? (
          <Link to={treasurerPath} className="header-link">
            <FaChartBar aria-hidden="true" /> Treasurer Reports
          </Link>
        ) : null}
      </div>
      <p className="page-intro">
        Set the monthly fee, season dates and Stripe account that parents pay into.
        The Team Monthly Subscription menu appears on assigned teams after Stripe is connected.
        {form.season_label ? ` ${form.season_label}.` : ''}
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <div className="tabs">
            <button
              type="button"
              className={activeTab === 'club' ? 'active' : ''}
              onClick={() => setActiveTab('club')}
            >
              Club
            </button>
            <button
              type="button"
              className={activeTab === 'stripe' ? 'active' : ''}
              onClick={() => setActiveTab('stripe')}
            >
              Stripe
            </button>
            <button
              type="button"
              className={activeTab === 'fees' ? 'active' : ''}
              onClick={() => setActiveTab('fees')}
            >
              Fees
            </button>
            <button
              type="button"
              className={activeTab === 'teams' ? 'active' : ''}
              onClick={() => setActiveTab('teams')}
            >
              Teams
            </button>
            <button
              type="button"
              className={activeTab === 'players' ? 'active' : ''}
              onClick={() => setActiveTab('players')}
            >
              Players
            </button>
            <button
              type="button"
              className={activeTab === 'users' ? 'active' : ''}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              type="button"
              className={activeTab === 'uploads' ? 'active' : ''}
              onClick={() => setActiveTab('uploads')}
            >
              Uploads
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'club' && (
              <form className="settings-form" onSubmit={handleSave}>
                <h3 className="tab-title">{form.name || 'Club'}</h3>
                <label className="field">
                  Academy name
                  <input value={form.name} onChange={(e) => updateField('name', e.target.value)} required />
                  <small>
                    Parents see {form.name.trim() ? academyProductLabel(form.name) : 'the academy name plus Monthly Subscription, for example SLJFC Monthly Subscription'} on the subscription page, so it is not confused with First Whistle Pro.
                  </small>
                </label>

                <p className="hint">
                  Season starts on the 1st of the start month and finishes on the last day of the finish month.
                  Parents are billed for every month in between, including both.
                  This season: {seasonInclusiveRange(form.season_start_month, form.season_end_month)}.
                </p>
                <div className="field-row">
                  <label className="field">
                    Season start (from the 1st)
                    <select
                      value={form.season_start_month}
                      onChange={(e) => updateField('season_start_month', Number(e.target.value))}
                    >
                      {MONTHS.map((month) => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    Season finish (through the last day)
                    <select
                      value={form.season_end_month}
                      onChange={(e) => updateField('season_end_month', Number(e.target.value))}
                    >
                      {MONTHS.map((month) => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <p className="hint">Shown to parents of {form.name || 'this academy'} only. Each academy keeps its own contact.</p>
                <label className="field">
                  Contact name
                  <input value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} />
                </label>
                <div className="field-row">
                  <label className="field">
                    Email
                    <input type="email" value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} />
                  </label>
                  <label className="field">
                    Phone
                    <input value={form.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} />
                  </label>
                </div>

                <button className="save-button" type="submit" disabled={saving}>
                  <FaSave /> {saving ? 'Saving…' : 'Save club settings'}
                </button>
              </form>
            )}

            {activeTab === 'fees' && (
              <form className="settings-form" onSubmit={(e) => handleSave(e, { includeContact: false })}>
                <h3 className="tab-title">Monthly fee</h3>
                {subscriptionsReady ? null : (
                  <p className="stripe-notice" role="status">
                    Stripe needs to be integrated before monthly fees can be collected. Connect an account on the Stripe tab.
                  </p>
                )}
                <label className="field">
                  Default monthly amount (£)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount_pounds}
                    onChange={(e) => updateField('amount_pounds', e.target.value)}
                  />
                </label>
                <label className="toggle-row">
                  <input
                    type="checkbox"
                    checked={form.fee_included}
                    onChange={(e) => updateField('fee_included', e.target.checked)}
                  />
                  <span>
                    <strong>Include the First Whistle fee in this price</strong>
                    <small>
                      {form.fee_included
                        ? 'Parents pay the amount you enter. The 5% fee is taken from that price.'
                        : 'Leave unticked to add the 5% fee on top. Parents then see the club fee and the First Whistle fee.'}
                    </small>
                  </span>
                </label>
                <div className="membership-parent-container academy-fee-checkout">
                  <div className="membership-parent-card">
                    <p className="lead">Based on the default monthly amount.</p>
                    <div className="rows">
                      <div className="row">
                        <span>Default amount</span>
                        <strong>{formatGbpFromPence(feeBreakdown.enteredPence)}</strong>
                      </div>
                      <div className="row">
                        <span>Club receives</span>
                        <strong>{formatGbpFromPence(feeBreakdown.clubPence)}</strong>
                      </div>
                      <div className="row">
                        <span>
                          First Whistle fee
                          <small>{`${form.platform_fee_percent || 5}%${form.fee_included ? ', taken from the price' : ', added on top'}`}</small>
                        </span>
                        <strong>{formatGbpFromPence(feeBreakdown.fwPence)}</strong>
                      </div>
                      <div className="row">
                        <span>
                          Stripe fee
                          <small>Typical UK card, 1.5% + 20p, taken from the First Whistle fee</small>
                        </span>
                        <strong>{formatGbpFromPence(feeBreakdown.stripePence)}</strong>
                      </div>
                      <div className="row">
                        <span>First Whistle keeps</span>
                        <strong>{formatGbpFromPence(feeBreakdown.fwKeepsPence)}</strong>
                      </div>
                      <div className="row amount">
                        <span>Parent pays</span>
                        <div className="amount-col">
                          <strong>{`${formatGbpFromPence(feeBreakdown.parentPence)} per month`}</strong>
                          {form.fee_included ? null : (
                            <button
                              type="button"
                              className="breakdown-button"
                              onClick={() => setFeeModalOpen(true)}
                              aria-haspopup="dialog"
                              aria-expanded={feeModalOpen}
                            >
                              {`${formatGbpFromPence(feeBreakdown.clubPence)} + ${formatGbpFromPence(feeBreakdown.fwPence)} fee`}
                              <FaInfoCircle aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <MembershipFeeModal
                  open={feeModalOpen}
                  onClose={() => setFeeModalOpen(false)}
                  clubLabel={form.name.trim() ? `${form.name.trim()} fee` : 'Club fee'}
                  clubAmount={formatGbpFromPence(feeBreakdown.clubPence)}
                  feeAmount={formatGbpFromPence(feeBreakdown.fwPence)}
                  totalAmount={`${formatGbpFromPence(feeBreakdown.parentPence)} per month`}
                  totalLabel="Parent pays"
                  variant="academy"
                />
                <h3 className="tab-title">Team fees</h3>
                <p className="hint">
                  {form.fee_included
                    ? 'Leave a team blank to use the default. The amount is the price parents pay. A bespoke fee for one player is set on the Players tab.'
                    : 'Leave a team blank to use the default. U16 can be more than U8. A bespoke fee for one player is set on the Players tab.'}
                </p>
                {selectedTeamIds.length === 0 ? (
                  <p className="hint">Assign teams first, then set a fee for each age group.</p>
                ) : (
                  <ul className="team-fee-list">
                    {feeTeams.map((team) => {
                      const teamId = Number(team.team_id);
                      const label = `${team.team_name}${team.age_group ? ` · ${team.age_group}` : ''}`;
                      return (
                        <li key={teamId}>
                          <span>
                            {label}
                            <small>Blank uses £{form.amount_pounds || '0.00'}</small>
                          </span>
                          <label className="field team-fee-field">
                            Amount (£)
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={teamAmounts[teamId] ?? ''}
                              placeholder={form.amount_pounds || '0.00'}
                              onChange={(e) => {
                                const next = e.target.value;
                                setTeamAmounts((current) => ({ ...current, [teamId]: next }));
                              }}
                            />
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <h3 className="tab-title">Sibling discount</h3>
                <p className="hint">
                  {form.fee_included
                    ? 'The oldest child pays the price above. Each younger sibling on the same parent account gets a percent off that price. Leave at 0 to turn this off.'
                    : 'The oldest child pays the full team fee. Each younger sibling on the same parent account gets a percent off their own club fee. Leave at 0 to turn this off.'}
                </p>
                <div className="field-row">
                  <label className="field">
                    Second child (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.sibling_discount_second_percent}
                      placeholder="10"
                      onChange={(e) => updateField('sibling_discount_second_percent', e.target.value)}
                    />
                  </label>
                  <label className="field">
                    Third child or more (%)
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.sibling_discount_third_percent}
                      placeholder="20"
                      onChange={(e) => updateField('sibling_discount_third_percent', e.target.value)}
                    />
                  </label>
                </div>
                <button className="save-button" type="submit" disabled={saving}>
                  <FaSave /> {saving ? 'Saving…' : 'Save fees'}
                </button>
              </form>
            )}

            {activeTab === 'players' && (
              <AcademyPlayers
                academyId={id}
                subscriptionsEnabled={subscriptionsReady}
                defaultAmountPounds={form.amount_pounds}
                teamAmounts={teamAmounts}
              />
            )}

            {activeTab === 'stripe' && (
              <div className="settings-form">
                <h3 className="tab-title">Stripe</h3>
                <p className="hint">
                  Connect the academy’s own Stripe account. First Whistle does not create one.
                  Use a different email from the First Whistle platform account.
                </p>
                {form.stripe_connected ? (
                  <div className="stripe-status is-connected">
                    <FaCheckCircle aria-hidden="true" />
                    <div>
                      <strong>Connected</strong>
                      <p>Payments go straight into your Stripe account.</p>
                      {form.stripe_connect_account ? (
                        <p className="stripe-account">{form.stripe_connect_account}</p>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <p className="hint">Not connected yet.</p>
                )}
                <button className="secondary-button" type="button" onClick={handleConnect} disabled={connecting}>
                  <FaCreditCard /> {connecting ? 'Redirecting…' : (form.stripe_connected ? 'Reconnect Stripe' : 'Connect with Stripe')}
                </button>
              </div>
            )}

            {activeTab === 'teams' && (
              <div className="settings-form">
                <h3 className="tab-title">Assigned teams</h3>
                <AcademyTeamPicker
                  teams={allTeams}
                  selectedTeamIds={selectedTeamIds}
                  onToggle={toggleTeam}
                  hint="Parents on these teams see Team Monthly Subscription once Stripe is connected. Existing First Whistle teams only."
                />
                <button className="save-button" type="button" onClick={handleSaveTeams} disabled={savingTeams}>
                  {savingTeams ? 'Saving teams…' : 'Save teams'}
                </button>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="settings-form">
                <h3 className="tab-title">Assigned users</h3>
                <p className="hint">
                  A user can have more than one role. Academy Admin can edit this academy.
                  Club Secretary can see parent subscription status and names.
                  Treasurer can open Treasurer Reports from Admin.
                  They are recognised when they log in as a parent if the phone matches.
                </p>
                <AcademyPersonSearch people={people} onChange={setPeople} academyId={id} />
                <button className="save-button" type="button" onClick={handleSavePeople} disabled={savingPerson || !people.length}>
                  {savingPerson ? 'Saving…' : 'Save assigned users'}
                </button>
              </div>
            )}

            {activeTab === 'uploads' && (
              <AcademyUploads academyId={id} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const AcademySubscriptionEditPage = () => {
  const { id } = useParams();
  return <AcademySubscriptionEdit key={id || 'new'} />;
};

export default AcademySubscriptionEditPage;
